import React, { useCallback, useMemo, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { reportsAPI } from '../../services/api';
import useReport from './shared/useReport';
import ChartPanel from './shared/ChartPanel';
import ReportTable from './shared/ReportTable';
import ReportPrintLayout from './shared/ReportPrintLayout';
import PrintButton from './shared/PrintButton';
import DateRangeFilter, { getPresetRange } from './shared/DateRangeFilter';
import { axisProps, fmtMoney, fmtMoneyShort, tooltipStyle, barColor } from './shared/chartTheme';
import useDrill from './shared/useDrill';

// Who the hospital's revenue actually comes from: per-patient totals across both tills
// (OPD fees net of refunds, plus invoice payments received in the range).

const COLUMNS = [
  { key: 'patient', label: 'Patient' },
  { key: 'mrn', label: 'MRN' },
  { key: 'phone', label: 'Phone' },
  { key: 'visits', label: 'Visits', align: 'right' },
  { key: 'opdNet', label: 'OPD Fees', align: 'right', render: (r) => fmtMoney(r.opdNet) },
  { key: 'invoices', label: 'Invoices', align: 'right' },
  { key: 'invoicePaid', label: 'Invoice Paid', align: 'right', render: (r) => fmtMoney(r.invoicePaid) },
  { key: 'total', label: 'Total', align: 'right', render: (r) => <span className="font-semibold">{fmtMoney(r.total)}</span>, csv: (r) => r.total },
  { key: 'lastActivity', label: 'Last Activity' },
];

const TopPayersReport = () => {
  const drill = useDrill();
  const [range, setRange] = useState(() => getPresetRange('thisMonth'));
  const [preset, setPreset] = useState('thisMonth');
  const [limit, setLimit] = useState(25);

  const fetcher = useCallback(
    () => reportsAPI.getTopPayers({ from: range.from, to: range.to, limit }),
    [range, limit]
  );
  const { data, loading } = useReport(fetcher);

  const top10 = useMemo(
    () => (data?.rows || []).slice(0, 10).map((r) => ({ name: r.patient, total: Number(r.total) })),
    [data]
  );

  return (
    <div className="space-y-5">
      <DateRangeFilter value={range} preset={preset} onChange={(r, p) => { setRange(r); setPreset(p); }} />

      <div className="flex items-center gap-3">
        <div className="bg-white rounded-xl shadow p-1 inline-flex">
          {[25, 50, 100].map((n) => (
            <button
              key={n}
              onClick={() => setLimit(n)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                limit === n ? 'bg-navy-800 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Top {n}
            </button>
          ))}
        </div>
        <div className="ml-auto"><PrintButton /></div>
      </div>

      <ChartPanel title="Top 10 by total spend">
        {loading || top10.length === 0 ? (
          <p className="text-gray-400 text-sm text-center py-16">
            {loading ? 'Loading' : 'No spending in the selected range'}
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(220, top10.length * 34)}>
            <BarChart data={top10} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" horizontal={false} />
              <XAxis type="number" {...axisProps} tickFormatter={fmtMoneyShort} />
              <YAxis type="category" dataKey="name" {...axisProps} width={150} />
              <Tooltip {...tooltipStyle} formatter={(v) => fmtMoney(v)} />
              <Bar dataKey="total" radius={[0, 4, 4, 0]} barSize={18}>
              {top10.map((_, i) => <Cell key={i} fill={barColor(i)} />)}
            </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartPanel>

      <ReportTable
        onRowClick={(r) => drill.open({ type: 'patient', mrn: r.mrn })}
        title={`Top ${limit} patients by spend`}
        columns={COLUMNS}
        rows={data?.rows || []}
        csvName={`top-payers-${range.from}-to-${range.to}`}
      />

      <ReportPrintLayout
        title="Top Payers"
        subtitle={`Period: ${range.from} to ${range.to} · Top ${limit}`}
        tables={[{ title: 'Patients by spend', columns: COLUMNS, rows: data?.rows || [] }]}
      />

      {drill.node}
    </div>
  );
};

export default TopPayersReport;
